/**
 * 网络状态检测 Composable
 *
 * 功能:
 * - 监听浏览器在线/离线状态
 * - 断网时, 如果当前在视讯正片页面 (lobby/home/game), 按"退出视讯"处理回大厅;
 *   大厅自身的浏览类页面断网不做任何跳转 (只提示, 不强制登出)
 * - 提供响应式的网络状态
 *
 * 使用方式:
 * ```ts
 * import { useNetworkStatus } from '@/composables/useNetworkStatus'
 *
 * // 在 App.vue 中调用以启用全局网络监听
 * useNetworkStatus()
 *
 * // 或者只获取网络状态,不启用自动跳转
 * const { isOnline } = useNetworkStatus({ autoRedirect: false })
 * ```
 */

import { useRouter, useRoute } from 'vue-router'
import { GameMessage } from '@/components/GameMessage'
import { i18nGlobal } from '@/i18n'
import { LIVE_CONTENT_ROUTE_NAMES } from '@/router'
import { exitLiveAndRestoreLobby } from '@/composables/useLiveGamePlay'

/**
 * 配置选项
 */
interface UseNetworkStatusOptions {
  /** 是否在断网时于视讯正片页面自动退出视讯回大厅 (默认 true) */
  autoRedirect?: boolean
}

/**
 * 返回值类型
 */
interface UseNetworkStatusReturn {
  /** 是否在线 */
  isOnline: Ref<boolean>
}

/**
 * 使用网络状态检测
 * @param options - 配置选项
 */
export function useNetworkStatus(options: UseNetworkStatusOptions = {}): UseNetworkStatusReturn {
  const { autoRedirect = true } = options

  const router = useRouter()
  const route = useRoute()

  // 网络状态
  const isOnline = ref(navigator.onLine)

  /**
   * 处理离线事件
   *
   * 这个 composable 是 App.vue 里全局启用的, 覆盖全站所有页面 —— 之前不管在哪个
   * 页面, 断网都会硬跳 `/login` (大厅自己的登录页), 这是视讯还是独立老应用时的
   * 假设 (断网必须重连视讯 WS, 只能回登录页重来)。现在大厅浏览类页面断网不该被
   * 强制登出, 只在视讯正片页面 (lobby/home/game) 才按"退出视讯"处理回大厅,
   * 且不能再是硬跳 /login (那会把共用的大厅账号一起登出)。
   */
  const handleOffline = () => {
    isOnline.value = false
    console.warn('[Network] 网络已断开')

    // 显示断网提示
    GameMessage.error(i18nGlobal.t('network_disconnected'), { duration: 3000 })

    if (!autoRedirect) return
    if (!LIVE_CONTENT_ROUTE_NAMES.has(route.name as string)) return

    console.warn('[Network] 视讯正片页面断网, 退出视讯回大厅')
    exitLiveAndRestoreLobby()
      .catch(error => console.error('[Network] 断网后回大厅失败:', error))
      .finally(() => router.replace('/'))
  }

  /**
   * 处理上线事件
   */
  const handleOnline = () => {
    isOnline.value = true
    // console.log('[Network] 网络已恢复')
  }

  // 组件挂载时添加事件监听
  onMounted(() => {
    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    // 初始检查
    if (!navigator.onLine) {
      handleOffline()
    }
  })

  // 组件卸载时移除事件监听
  onUnmounted(() => {
    window.removeEventListener('offline', handleOffline)
    window.removeEventListener('online', handleOnline)
  })

  return {
    isOnline,
  }
}
